/**
 * Date formatting utilities for the ID camps section (WI-48) and
 * notification timestamps (WI-56).
 */

/**
 * Parse an ISO date string as local midnight (not UTC midnight).
 * Bare ISO dates like "2026-07-18" are UTC in most JS environments, which
 * shifts the displayed date by one day in timezones west of UTC.
 * Appending T00:00:00 forces local-time parsing.
 */
function parseLocalDate(dateStr: string): Date {
  return new Date(`${dateStr}T00:00:00`);
}

/**
 * Format an ISO date string as a short-month display date.
 * e.g. "2026-07-18" → "Jul 18, 2026"
 */
function formatSingleDate(dateStr: string): string {
  const d = parseLocalDate(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/** Sentinel date used for hub-link camps whose real date is unknown. */
export const DATE_UNKNOWN_SENTINEL = '9999-12-31';

/**
 * Format a camp date range.
 *
 * | Case                      | Output example              |
 * |---------------------------|-----------------------------|
 * | Single-day (null end)     | Jul 18, 2026                |
 * | Single-day (same dates)   | Jul 18, 2026                |
 * | Multi-day, same month     | Jul 18–19, 2026             |
 * | Multi-day, diff month     | Jun 30 – Jul 2, 2026        |
 * | Multi-year                | Dec 31, 2026 – Jan 1, 2027  |
 *
 * Belt-and-suspenders: returns empty string when called with the sentinel
 * date "9999-12-31". Components should guard with `camp.date_unknown` before
 * calling this function; this is a second layer of defense. WI-51b.
 */
export function formatCampDateRange(startDate: string, endDate: string | null): string {
  // Second-layer defense: never format the sentinel date.
  if (startDate === DATE_UNKNOWN_SENTINEL) {
    return '';
  }

  if (!endDate || endDate === startDate) {
    return formatSingleDate(startDate);
  }

  const start = parseLocalDate(startDate);
  const end = parseLocalDate(endDate);

  const startYear = start.getFullYear();
  const endYear = end.getFullYear();
  const startMonth = start.getMonth();
  const endMonth = end.getMonth();

  const fmt = (d: Date, opts: Intl.DateTimeFormatOptions) =>
    d.toLocaleDateString('en-US', opts);

  if (startYear !== endYear) {
    // Cross-year: Dec 31, 2026 – Jan 1, 2027
    return `${formatSingleDate(startDate)} – ${formatSingleDate(endDate)}`;
  }

  if (startMonth === endMonth) {
    // Same month: Jul 18–19, 2026
    const monthYear = fmt(start, { month: 'short', year: 'numeric' });
    const startDay = fmt(start, { day: 'numeric' });
    const endDay = fmt(end, { day: 'numeric' });
    // monthYear is like "Jul 2026"; reconstruct as "Jul 18–19, 2026"
    const month = fmt(start, { month: 'short' });
    const year = startYear;
    return `${month} ${startDay}–${endDay}, ${year}`;
  }

  // Different months, same year: Jun 30 – Jul 2, 2026
  const startPart = fmt(start, { month: 'short', day: 'numeric' });
  const endPart = fmt(end, { month: 'short', day: 'numeric' });
  return `${startPart} – ${endPart}, ${endYear}`;
}

/**
 * Format price in cents to a display string.
 *
 * | Input  | Output          |
 * |--------|-----------------|
 * | null   | Price not listed|
 * | 0      | Free            |
 * | 17500  | $175            |
 * | 15099  | $150.99         |
 */
export function formatPrice(cents: number | null): string {
  if (cents === null) return 'Price not listed';
  if (cents === 0) return 'Free';
  const dollars = Math.floor(cents / 100);
  const remainingCents = cents % 100;
  return remainingCents === 0
    ? `$${dollars}`
    : `$${dollars}.${String(remainingCents).padStart(2, '0')}`;
}

/**
 * Normalize ASCII hyphens in age-group range strings to en-dashes.
 * e.g. "9th-12th grade" → "9th–12th grade"
 */
export function normalizeAgeGroup(ageGroup: string): string {
  return ageGroup.replace(/(\d+)-(\d+)/g, '$1–$2');
}

/**
 * Format an ISO datetime string as a human-readable relative time.
 *
 * Thresholds:
 *   < 1 minute  → "just now"
 *   1–59 min    → "Xm ago"
 *   1–23 hours  → "Xh ago"
 *   1–6 days    → "Xd ago"
 *   ≥ 7 days    → "Xwk ago"
 *
 * Used for notification timestamps in the notification center (WI-56).
 */
export function relativeTime(isoString: string): string {
  const now = Date.now();
  const then = new Date(isoString).getTime();
  const diffMs = now - then;

  if (diffMs < 0) return 'just now';

  const diffSeconds = Math.floor(diffMs / 1000);
  if (diffSeconds < 60) return 'just now';

  const diffMinutes = Math.floor(diffSeconds / 60);
  if (diffMinutes < 60) return `${diffMinutes}m ago`;

  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours}h ago`;

  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;

  const diffWeeks = Math.floor(diffDays / 7);
  return `${diffWeeks}wk ago`;
}
